import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loading } from 'components/uikit/suspense/Loading';
import { AppTable } from 'components/uikit/table/AppTable';
import { QueryFactory } from 'services/api';
import { TemplateVersionHistoryItemDto } from 'services/api/api-client.types';
import { useVersionHistoryTableColumns } from './useVersionHistoryTableColumns';
import tableStyles from 'styles/tables.module.scss';
import styles from './VersionHistoryModal.module.scss';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

type VersionHistoryModalProps = {
  templateId: number;
  isOpen: boolean;
  onClose: () => void;
};

export const VersionHistoryModal: React.FC<VersionHistoryModalProps> = (
  props,
) => {
  const versionsQuery = QueryFactory.TemplateQuery.useGetVersionHistoryQuery(
    props.templateId,
    {
      enabled: props.isOpen,
    },
  );
  const i18n = useScopedTranslation('Page.Templates.VersionHistoryModal');
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);

  const columns = useVersionHistoryTableColumns({
    secondToLastVersionId: versionsQuery.data?.[1]?.id,
    setIsLoading,
    onVersionDelete: async () => {
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.TemplateQuery.getVersionHistoryQueryKey(
          props.templateId,
        ),
      });
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.TemplateQuery.getFoldersQueryKey(),
      });
    },
  });

  const table = useReactTable<TemplateVersionHistoryItemDto>({
    getCoreRowModel: getCoreRowModel(),
    data: versionsQuery.data ?? [],
    columns,
  });

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={i18n.t('title')}
      isBlocking={true}
      classes={{
        paper: styles.modalPaper,
        content: styles.modalContent,
        header: styles.modalHeader,
      }}
    >
      <Loading loading={versionsQuery.isLoading || isLoading}>
        <AppTable
          table={table}
          classes={{
            wrapper: tableStyles.wrapper,
            table: tableStyles.table,
            cell: styles.cell,
            header: styles.header,
          }}
        />
      </Loading>
    </CustomModal>
  );
};
